import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useAdminTheme } from '@shared/providers/admin-theme/AdminThemeContext';
import { useSiteAdminAuth } from '@shared/providers/SiteAdminAuthContext';
import { useTenantSiteCheck } from '@/hooks/admin/useTenantSiteCheck';
import { AdminLoginCredentials } from '@app-types/admin-auth';
import { adminLoginFormSchema, type AdminLoginFormData } from '@/schemas/adminLoginSchema';
import { AppButton, AppSpin, AppTextField, CountdownErrorMessage } from '@/components/atoms';
import { Footer } from '@/components/organisms';
import {
  buildSsoLoginUrl,
  isSsoSite,
  markPassiveSsoCheck,
  shouldShowPassiveSsoSplash,
  shouldStartPassiveSsoCheck,
  stripPassiveMarkerFromUrl,
} from '@/features/sso/ssoSession';
import { rememberSsoIntendedPath } from '@/features/sso/activeSessionSync';

const SITE_NAMES: Record<string, string> = {
  medix: 'Medix',
  hitowa: 'Hitowa',
};

/**
 * Site Admin Login Page Component
 *
 * Site-specific admin portal login page.
 * URL: /{site}/admin/login
 * Uses email + password authentication with site validation.
 */
const SiteAdminLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { site } = useParams<{ site: string }>();
  const { colors } = useAdminTheme();
  const {
    currentSite,
    isAuthenticated,
    login,
    isLoading,
    error,
  } = useSiteAdminAuth();

  const {
    data: siteCheck,
    isLoading: isCheckingSite,
    isError: isSiteCheckError,
  } = useTenantSiteCheck(site, 'admin');

  const validSite = useMemo(() => {
    if (!site) return null;
    if (isCheckingSite) return null;
    if (isSiteCheckError) return null;
    if (!siteCheck?.success) return null;
    if (!siteCheck.data?.exists) return null;
    return site;
  }, [site, isCheckingSite, isSiteCheckError, siteCheck]);

  const [passiveRedirectStarted, setPassiveRedirectStarted] = useState(false);
  // Captured ONCE. `stripPassiveMarkerFromUrl()` below rewrites the address bar the moment a
  // passive miss lands here, and react-router's `location.search` follows it. Reading it live
  // made the gate flip back to "not checked yet" on the very next render and fire another probe:
  // probe -> broker -> ?sso_passive=checked -> strip -> probe... Measured on dev3 2026-08-28 as
  // ELEVEN main-frame navigations and five broker round trips for one reload after an SLO
  // elsewhere -- the screen flicker in the report, ending only when the server-side rate limiter
  // cut it off. P3 hit this first and settled on exactly this snapshot (passiveSsoLoginState).
  const [ssoInitialSearch] = useState(() => location.search);
  // Captured with the same snapshot discipline: the guard puts the blocked location in
  // `state.from`, and it must be read before anything replaces this page.
  const [intendedPath] = useState(() => {
    const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
    return typeof from?.pathname === 'string'
      ? `${from.pathname}${from.search ?? ''}`
      : '';
  });
  const manualLoginRef = useRef(false);
  const [manualLoginActive, setManualLoginActive] = useState(false);
  const activateManualLogin = () => {
    manualLoginRef.current = true;
    setManualLoginActive(true);
  };
  const canStartPassiveSsoCheck =
    isSsoSite(site) &&
    (isCheckingSite || validSite !== null) &&
    shouldStartPassiveSsoCheck({
      context: { site, entryType: 'admin' },
      ssoEnabled: import.meta.env.VITE_SSO_ENABLED,
      passiveSsoEnabled: import.meta.env.VITE_SSO_PASSIVE_ENABLED,
      hasLocalCredential: Boolean(localStorage.getItem('site_admin_token')),
      manualLoginActive,
      search: ssoInitialSearch,
      storage: sessionStorage,
    });
  const showPassiveSsoSplash = shouldShowPassiveSsoSplash({
    canStartPassiveSsoCheck,
    passiveRedirectStarted,
  });

  useEffect(() => {
    if (
      !canStartPassiveSsoCheck ||
      passiveRedirectStarted ||
      manualLoginRef.current ||
      !validSite ||
      !isSsoSite(validSite)
    ) {
      return;
    }

    setPassiveRedirectStarted(true);
    // Carry the deep link across the broker round trip. `state.from` is what the route guard
    // saved when it bounced an unauthenticated visitor here, and it dies with the page on the
    // whole-page redirect below -- so it has to be written down BEFORE that happens.
    rememberSsoIntendedPath(
      { site: validSite, entryType: 'admin' },
      intendedPath,
      sessionStorage,
    );
    markPassiveSsoCheck(
      { site: validSite, entryType: 'admin' },
      sessionStorage,
    );
    const apiOrigin = import.meta.env.VITE_API_URL?.trim() || window.location.origin;
    window.location.assign(buildSsoLoginUrl(apiOrigin, {
      site: validSite,
      entryType: 'admin',
      prompt: 'none',
    }));
  }, [canStartPassiveSsoCheck, passiveRedirectStarted, validSite]);

  // Strip a resolved `sso_passive=checked` so a later reload re-attempts passive.
  useEffect(() => {
    stripPassiveMarkerFromUrl();
  }, []);

  const from = (location.state as { from?: Location })?.from?.pathname || `/${site}/admin`;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useTrimmedForm<AdminLoginFormData>({
    resolver: zodResolver(adminLoginFormSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: AdminLoginFormData) => {
    activateManualLogin();
    if (!validSite) return;

    const credentials: AdminLoginCredentials = {
      email: data.email,
      password: data.password,
    };

    const success = await login(credentials, validSite);
    if (success) {
      navigate(from, { replace: true });
    }
  };

  if (isAuthenticated && currentSite === validSite) {
    return <Navigate to={from} replace />;
  }

  if (showPassiveSsoSplash || isCheckingSite) {
    return <AppSpin fullscreen size="large" />;
  }

  if (!validSite || isSiteCheckError) {
    return (
      <div className="login-page-bg min-h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-white bg-opacity-95 rounded-lg shadow-lg py-6 px-8 text-center">
            <h1 className="text-xl font-bold text-red-600 mb-4">無効なサイト</h1>
            <p className="text-gray-600">指定されたサイトは存在しません。</p>
            <AppButton
              type="primary"
              onClick={() => navigate('/')}
              className="mt-4"
              style={{ backgroundColor: colors.primary }}
            >
              管理者ポータルへ
            </AppButton>
          </div>
        </div>
        <Footer fixed={false} />
      </div>
    );
  }

  const siteName = SITE_NAMES[validSite] ?? validSite;

  return (
    <div className="login-page-bg min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full px-4 max-w-[460px] mt-2.5">
          <div className="text-center mb-3">
            <img
              src="/images/login/logo.png"
              alt={`${siteName} Admin`}
              className="mx-auto w-[380px] max-w-full"
            />
          </div>

          <div className="bg-white bg-opacity-95 rounded-lg shadow-lg py-6 px-8">
            <div className="text-center mb-4">
              <span
                className="text-white px-3 py-1 rounded-full text-sm font-medium"
                style={{ backgroundColor: colors.primary }}
              >
                {siteName} 管理者ポータル
              </span>
            </div>

            {error && (
              <div
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-3"
                role="alert"
                data-testid="site-admin-login-error"
              >
                <CountdownErrorMessage message={error} />
              </div>
            )}

            <form
              onFocusCapture={activateManualLogin}
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-3"
            >
              <div>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <AppTextField
                      {...field}
                      id="email"
                      type="email"
                      placeholder="メールアドレス"
                      status={errors.email ? 'error' : undefined}
                      className="w-full"
                      autoComplete="email"
                      data-testid="site-admin-email-input"
                    />
                  )}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>

              <div>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <AppTextField
                      {...field}
                      id="password"
                      type="password"
                      placeholder="パスワード"
                      status={errors.password ? 'error' : undefined}
                      className="w-full"
                      autoComplete="current-password"
                      data-testid="site-admin-password-input"
                    />
                  )}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">{errors.password.message}</p>
                )}
              </div>

              <div className="flex justify-center pt-3">
                <AppButton
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  disabled={isLoading}
                  className="py-2 px-8 hover:opacity-90"
                  style={{ backgroundColor: colors.primary }}
                  data-testid="site-admin-login-submit"
                >
                  {isLoading ? 'ログイン中...' : 'ログイン'}
                </AppButton>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Footer fixed={false} />
    </div>
  );
};

export default SiteAdminLoginPage;
